import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  createTheme,
  useMediaQuery,
} from '@mui/material';
import './style.css';
import LeadStore from '../../store/LeadStore';
import { isEmpty } from '../../utils/helper';
import MultiSelectDropdown from './MultiSelectDropdown';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export const EditSiteDetails = observer(({ leadId, details, handleEditSiteDetails }) => {
  const theme = createTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState({
    id: details?.clientSiteDetailId ?? null,
    leadId,
    siteAddress: details?.siteAddress,
    siteNumber: details?.siteNumber,
    siteOwnerNamePrimary: details?.siteOwnerNamePrimary,
    siteOwnerNameSecondary: details?.siteOwnerNameSecondary,
    constructionSiteGpsLocation: details?.constructionSiteGpsLocation,
    documentsReceived: details?.documentsReceived?.split(','),
    clientPersonalDocuments: details?.clientPersonalDocuments?.split(','),
  });

  const [errors, setErrors] = useState({
    siteAddress: false,
    siteNumber: false,
    siteOwnerNamePrimary: false,
    documentsReceived: false,
    clientPersonalDocuments: false,
  });

  const [errorMessages, setErrorMessages] = useState({
    siteAddress: '',
    siteNumber: '',
    siteOwnerNamePrimary: '',
    documentsReceived: '',
    clientPersonalDocuments: '',
  });

  const documentsReceivedList = [
    { id: 1, name: 'Katha-A OR Katha-B' },
    { id: 2, name: 'Tax Receipt' },
    { id: 3, name: 'Mother Deed' },
    { id: 4, name: 'Sale Deed' },
    { id: 5, name: 'Gift Deed' },
    { id: 6, name: 'Encumbrance Certificate' },
    { id: 7, name: 'NOC' },
    { id: 8, name: 'Position Letter' },
    { id: 9, name: 'Correct Dimension Report' },
    { id: 10, name: 'Old Owner Documents' },
    { id: 11, name: 'Plan Approval' },
    { id: 12, name: 'Blue Print' },
    { id: 13, name: 'Schedule Of Property' },
    { id: 14, name: 'C D Patti' },
  ];

  const clientPersonalDocumentsList = [
    { id: 3, name: 'PAN Card' },
    { id: 2, name: 'Aadhar Card' },
    { id: 1, name: 'Check Bandi ( Schedule of property )' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Perform validation
    setErrors({ ...errors, [name]: isEmpty(value) });
  };

  const handleUpdateLeadKycSubmit = (e) => {
    e.preventDefault();

    const updatedErrors = {
      siteAddress: isEmpty(formData.siteAddress),
      siteNumber: isEmpty(formData.siteNumber),
      siteOwnerNamePrimary: isEmpty(formData.siteOwnerNamePrimary),
    };
    setErrors(updatedErrors);

    if (!Object.values(updatedErrors).some((error) => error === true)) {
      const payload = { ...formData };
      payload.documentsReceived = payload.documentsReceived?.join(',');
      payload.clientPersonalDocuments = payload.clientPersonalDocuments?.join(',');
      isEmpty(formData.id) ? LeadStore.createSiteDetails(payload) : LeadStore.updateSiteDetails(payload);
    }
  };

  useEffect(() => {
    LeadStore.updateSiteDetailsStatus && handleEditSiteDetails();
  }, [LeadStore.updateSiteDetailsStatus]);

  return (
    <Paper sx={{ px: '30px', py: '20px', pb: '40px' }}>
      <Box
        id="lead-kyc-details-form"
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          handleUpdateLeadKycSubmit(event);
        }}
      >
        <Stack spacing={4}>
          <Stack spacing={3}>
            <Grid rowGap={2} container>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pr: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <TextField
                    label="Site Address"
                    required
                    id="siteAddress"
                    size="small"
                    variant="standard"
                    name="siteAddress"
                    value={formData.siteAddress ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.siteAddress)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Site Number"
                    id="siteNumber"
                    size="small"
                    type="number"
                    variant="standard"
                    sx={{ fontSize: '12px' }}
                    name="siteNumber"
                    maxLength="300"
                    value={formData.siteNumber ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.siteNumber)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Site Owner Name(Primary)"
                    required
                    id="siteOwnerNamePrimary"
                    size="small"
                    variant="standard"
                    name="siteOwnerNamePrimary"
                    value={formData.siteOwnerNamePrimary ?? ''}
                    onChange={(e) => handleChange(e)}
                    error={Boolean(errors.siteOwnerNamePrimary)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Site Owner Name(Secondary)"
                    id="siteOwnerNameSecondary"
                    size="small"
                    variant="standard"
                    name="siteOwnerNameSecondary"
                    value={formData.siteOwnerNameSecondary ?? ''}
                    onChange={(e) => handleChange(e)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pl: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <TextField
                    label="Construction Site Gps Location"
                    id="constructionSiteGpsLocation"
                    size="small"
                    variant="standard"
                    name="constructionSiteGpsLocation"
                    value={formData.constructionSiteGpsLocation ?? ''}
                    onChange={(e) => handleChange(e)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <FormControl variant="standard" required error={Boolean(errors.documentsReceived)}>
                    <InputLabel id="documentsReceivedLabel" required shrink={true}>
                      Documents Received from clients
                    </InputLabel>
                    <Select
                      labelId="documentsReceivedLabel"
                      id="documentsReceived"
                      name="documentsReceived"
                      required
                      value={formData.documentsReceived ?? []}
                      multiple
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          documentsReceived: typeof value === 'string' ? value.split(',') : value,
                        });
                        setErrors({
                          ...errors,
                          documentsReceived: isEmpty(value),
                        });
                      }}
                      label="Documents Received from clients"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {documentsReceivedList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <Checkbox checked={formData.documentsReceived?.indexOf(site.name) > -1} />
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl variant="standard" required error={Boolean(errors.clientPersonalDocuments)}>
                    <InputLabel id="clientPersonalDocumentsLabel" required shrink={true}>
                      Clients Personal Documents
                    </InputLabel>
                    <Select
                      labelId="clientPersonalDocumentsLabel"
                      id="clientPersonalDocuments"
                      name="clientPersonalDocuments"
                      required
                      value={formData.clientPersonalDocuments ?? []}
                      multiple
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({
                          ...formData,
                          clientPersonalDocuments: typeof value === 'string' ? value.split(',') : value,
                        });
                        setErrors({
                          ...errors,
                          clientPersonalDocuments: isEmpty(value),
                        });
                      }}
                      label="Clients Personal Documents"
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                      MenuProps={MenuProps}
                    >
                      {clientPersonalDocumentsList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <Checkbox checked={formData.clientPersonalDocuments?.indexOf(site.name) > -1} />
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <MultiSelectDropdown
                    data={{
                      label: 'Site Condition Before Construction',
                      required: false,
                      allowFreeField: true, // Allows users to enter their own values
                      allowMultipleSelection: true, // Multiple selections allowed
                      options: [
                        { label: 'Slope Land', required: false, options: [] },
                        {
                          label: '< 5 Km from City',
                          required: false,
                          options: [],
                        },
                        {
                          label: '< 10 Km from City',
                          required: false,
                          options: [],
                        },
                        {
                          label: 'Water Logging Site',
                          required: false,
                          options: [],
                        },
                        { label: 'Loose soil', required: false, options: [] },
                        {
                          label: 'Logistics Challenges',
                          required: false,
                          options: [],
                        },
                        {
                          label: 'Intermediate Site',
                          required: false,
                          options: [],
                        },
                        {
                          label: 'Corner Site',
                          required: false,
                          options: [],
                        },
                        {
                          label: 'Available: Provision for Under ground Drainage connections',
                          required: false,
                          options: [],
                        },
                        {
                          label: 'Not Available: Provision for Under ground Drainage connections',
                          required: false,
                          options: [],
                        },
                        {
                          label: 'Available: Space for Watchmen shed construction',
                          required: false,
                          options: [],
                        },
                        {
                          label: 'Not Available: Space for Watchmen shed construction',
                          required: false,
                          options: [],
                        },
                        {
                          label: 'Available: Water Supply',
                          required: false,
                          options: [],
                        },
                        {
                          label: 'Not Available: Water Supply',
                          required: false,
                          options: [],
                        },
                        {
                          label: 'Available: Electricity Supply',
                          required: false,
                          options: [],
                        },
                        {
                          label: 'Not Available: Electricity Supply',
                          required: false,
                          options: [],
                        },
                        {
                          label: 'Available: Space for storing Materials ',
                          required: false,
                          options: [],
                        },
                        {
                          label: 'Not Available: Space for storing Materials ',
                          required: false,
                          options: [],
                        },
                        {
                          label: 'Existing Compound: 1 Side ',
                          required: false,
                          options: [],
                        },
                        {
                          label: 'Existing Compound: 2 Side ',
                          required: false,
                          options: [],
                        },
                        {
                          label: 'Existing Compound: 3 Side ',
                          required: false,
                          options: [],
                        },
                        {
                          label: 'Existing Compound: 4 Side ',
                          required: false,
                          options: [],
                        },
                      ],
                    }}
                    name="siteCondition"
                    value={formData.siteCondition}
                    onChange={handleChange}
                    error={Boolean(errors.siteCondition)}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          <Stack direction={'row'} sx={{ ml: 'auto' }} alignSelf={isSmallScreen ? '' : 'end'} spacing={2}>
            <Button
              variant="outlined"
              color="inherit"
              fullWidth
              size="small"
              onClick={() => handleEditSiteDetails(false)}
            >
              Cancel
            </Button>
            <Button variant="contained" type="submit" fullWidth color="primary" size="small">
              Update
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
});
